import { useState, type ReactNode } from 'react';
import { Modal, Button } from '@/components';
import { toEasternInputValue } from './lib/sentAt';

/**
 * "Did you send it, and when?" — the step after an Outlook draft (#608).
 *
 * The composer only opens a mailto: draft; nothing tells the app whether the
 * email went. Until #608 the send was recorded when the draft OPENED, so an RFI
 * someone meant to send tomorrow read as sent today. Now the person confirms it
 * here, with the time it went — defaulting to now, editable for "I sent it last
 * night".
 *
 * Used by the RFI tab and, since #626, the Submittals tab, in two ways:
 *   • confirming a send — the cancel button is "Not sent yet", and nothing is
 *     recorded; Send stays on offer.
 *   • correcting one already recorded — prefilled with the recorded time, and,
 *     where allowed, "Mark as not sent" to clear it.
 *
 * ⚠️ The input is Eastern wall-clock (see lib/sentAt). The API does the
 * conversion and the checks (not in the future, not before the record existed);
 * `max` and `min` here only steer the picker, and the API's message is shown if
 * it refuses.
 */

export interface MarkSentModalProps {
  title: string;
  /** What is being confirmed, in a sentence. */
  intro: ReactNode;
  /** Initial value of the time field. Defaults to now, in Eastern. */
  initialValue?: string;
  /** The earliest time the picker offers — when the record was created. */
  min?: string;
  /**
   * The latest it can be, when something later is already recorded (an RFI's
   * logged response, a submittal's logged decision). The picker never offers past
   * now either, and the default value is the earlier of the two — "now" on an
   * answered record would only be refused by the API.
   */
  max?: string;
  confirmLabel: string;
  cancelLabel: string;
  onSubmit: (sentAt: string) => Promise<unknown>;
  /** Present only when the recorded send may be cleared. */
  onClear?: () => Promise<unknown>;
  onClose: () => void;
}

const css = `
  .msm-field { display: flex; flex-direction: column; gap: 4px; margin-top: var(--ew-s-3); }
  .msm-field label {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    text-transform: uppercase; letter-spacing: var(--ew-ls-caption); color: var(--ew-n-500);
  }
  .msm-input {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font);
    padding: 7px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    background: #fff; color: var(--ew-dark); max-width: 260px;
  }
  .msm-intro { font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.5 var(--ew-font); color: var(--ew-dark); margin: 0; }
  .msm-error {
    margin-top: var(--ew-s-3); padding: 8px 12px; background: #FBE4DF; color: #8F2414;
    border-radius: var(--ew-r-md); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
  }
  /* Secondary, in red: it undoes a recorded send. Not btn-ghost — that is the
     dark top bar's white-on-transparent style, and it was invisible here. */
  .btn.msm-clear { margin-right: auto; color: var(--ew-danger); }
`;

export default function MarkSentModal({
  title, intro, initialValue, min, max: limit, confirmLabel, cancelLabel, onSubmit, onClear, onClose,
}: MarkSentModalProps) {
  // Wall-clock strings of one fixed shape compare correctly as text.
  const earlier = (a: string, b?: string) => (b && b < a ? b : a);
  const [value, setValue] = useState(() => initialValue ?? earlier(toEasternInputValue(), limit));
  const [pending, setPending] = useState<'submit' | 'clear' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The picker's "latest" is now, as the API's is. Refreshed on focus, so a
  // dialog left open across midnight does not grey out the new day.
  const [now, setNow] = useState(() => toEasternInputValue());
  const max = earlier(now, limit);

  const run = async (which: 'submit' | 'clear') => {
    if (which === 'submit' && !value) {
      setError('Enter the date and time it was sent.');
      return;
    }
    setPending(which);
    setError(null);
    try {
      await (which === 'submit' ? onSubmit(value) : onClear!());
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setPending(null);
    }
  };

  return (
    <>
      <style>{css}</style>
      <Modal
        isOpen
        onClose={() => { if (!pending) onClose(); }}
        title={title}
        maxWidth={480}
        footer={
          <>
            {onClear && (
              <Button
                variant="secondary"
                className="msm-clear"
                onClick={() => run('clear')}
                disabled={pending !== null}
                isLoading={pending === 'clear'}
              >
                Mark as not sent
              </Button>
            )}
            <Button variant="secondary" onClick={onClose} disabled={pending !== null}>{cancelLabel}</Button>
            <Button onClick={() => run('submit')} disabled={pending !== null} isLoading={pending === 'submit'}>
              {confirmLabel}
            </Button>
          </>
        }
      >
        <p className="msm-intro">{intro}</p>
        <div className="msm-field">
          <label htmlFor="msm-sent-at">Sent (Eastern time)</label>
          <input
            id="msm-sent-at"
            className="msm-input"
            type="datetime-local"
            value={value}
            min={min}
            max={max}
            onFocus={() => setNow(toEasternInputValue())}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        {error && <div className="msm-error" role="alert">{error}</div>}
      </Modal>
    </>
  );
}
